import { useState, useEffect } from 'react';
import { api } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Share2, Calendar, Loader2, Wand2, Copy, Check } from 'lucide-react';

const PLATFORM_COLORS = {
  linkedin: 'bg-blue-100 text-blue-700',
  facebook: 'bg-indigo-100 text-indigo-700',
  twitter: 'bg-sky-100 text-sky-700',
};

const PLATFORM_ACTIVE = {
  linkedin: 'bg-blue-600 text-white border-blue-600',
  facebook: 'bg-indigo-600 text-white border-indigo-600',
  twitter: 'bg-sky-500 text-white border-sky-500',
};

export default function SocialMediaPage() {
  const { selectedProjectId } = useProject();
  const [tab, setTab] = useState('generate');
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [allPosts, setAllPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [copied, setCopied] = useState(null);
  const [publishedInfo, setPublishedInfo] = useState({});
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const [sourceContent, setSourceContent] = useState('');
  const [postUrl, setPostUrl] = useState('');
  const [tone, setTone] = useState('professional');
  const [platform, setPlatform] = useState('linkedin');

  const [calBusiness, setCalBusiness] = useState('');
  const [calTopics, setCalTopics] = useState('');
  const [calDays, setCalDays] = useState(14);

  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => { fetchPosts(); fetchStats(); }, []);

  const fetchPosts = async () => {
    try {
      const res = await api.get('/social/posts');
      setAllPosts(res.data.posts || []);
    } catch { /* ignore */ }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/social/stats');
      setStats(res.data.stats);
    } catch { /* ignore */ }
  };

  const handleGenerate = async () => {
    if (!sourceContent.trim()) return toast.error('Enter source content');
    setLoading(true);
    try {
      const res = await api.post('/social/generate', {
        source_content: sourceContent, platforms: [platform], url: postUrl, tone,
        project_id: selectedProjectId || undefined,
      });
      setPosts(res.data.posts || []);
      toast.success(`${res.data.posts.length} posts generated!`);
      fetchPosts(); fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally { setLoading(false); }
  };

  const handleCalendar = async () => {
    if (!calBusiness.trim() || !calTopics.trim()) return toast.error('Fill in business type and topics');
    setLoading(true);
    try {
      const res = await api.post('/social/calendar', {
        business_type: calBusiness,
        topics: calTopics.split(',').map(t => t.trim()),
        days: calDays,
      });
      setCalendar(res.data.calendar);
      toast.success('Calendar generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Calendar generation failed');
    } finally { setLoading(false); }
  };

  const copyText = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const updateStatus = async (postId, status) => {
    try {
      await api.put(`/social/posts/${postId}/status`, { status });
      toast.success(`Post ${status}`);
      fetchPosts(); fetchStats();
    } catch { toast.error('Update failed'); }
  };

  const publishToApi = async (postId) => {
    setLoading(true);
    try {
      const res = await api.post(`/social/posts/${postId}/publish`);
      const publishedUrl = res.data?.published_url || null;
      setPublishedInfo(prev => ({ ...prev, [postId]: { url: publishedUrl } }));
      toast.success('Published successfully via API!');
      fetchPosts(); fetchStats();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'object' ? detail?.message : detail;
      const upgradeUrl = typeof detail === 'object' ? detail?.upgrade_url : null;
      const errorCode = typeof detail === 'object' ? detail?.error_code : null;

      const needsIntegrationsLink = errorCode === 'TOKEN_EXPIRED' || errorCode === 'NOT_CONNECTED';

      if (upgradeUrl) {
        toast.error(
          <span>
            {message}{' '}
            <a href={upgradeUrl} target="_blank" rel="noopener noreferrer"
               className="underline font-semibold">
              {errorCode === 'UPGRADE_REQUIRED' ? 'Upgrade plan →' : 'Fix it →'}
            </a>
          </span>,
          { duration: 8000 }
        );
      } else if (needsIntegrationsLink) {
        toast.error(
          <span>
            {message}{' '}
            <a href="/dashboard/integrations" className="underline font-semibold">
              Go to Integrations →
            </a>
          </span>,
          { duration: 8000 }
        );
      } else {
        toast.error(message || 'Publish failed', { duration: 6000 });
      }
    } finally {
      setLoading(false);
    }
  };

  const filtered = allPosts.filter(p =>
    (!filterPlatform || p.platform === filterPlatform) &&
    (!filterStatus || p.status === filterStatus)
  );

  const tabs = [
    { key: 'generate', label: 'Generate Posts' },
    { key: 'calendar', label: 'Content Calendar' },
    { key: 'library', label: 'Post Library' },
  ];

  return (
    <motion.div className="space-y-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Social Media</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">AI-generated social posts, calendar planning & direct publishing</p>
        </div>
        {stats && (
          <div className="flex gap-3 flex-wrap">
            <Badge variant="outline">{stats.total} total posts</Badge>
            {Object.entries(stats.by_platform || {}).map(([p, c]) => (
              <Badge key={p} className={PLATFORM_COLORS[p] || 'bg-slate-100'}>{p}: {c}</Badge>
            ))}
          </div>
        )}
      </div>

      {/* Direct API notice */}
      <div className="rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/20 px-4 py-3 text-xs text-violet-700 dark:text-violet-300 flex items-center gap-2">
        <span className="font-semibold">Direct Publish:</span>
        Connect Facebook, Twitter/X, or LinkedIn on the{' '}
        <a href="/dashboard/integrations" className="underline font-medium">Integrations page</a>{' '}
        to enable one-click "API Publish" directly to your social accounts.
      </div>

      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-800 pb-1">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-sm font-medium rounded-t transition-colors ${
              tab === t.key ? 'text-violet-600 border-b-2 border-violet-600 bg-violet-50 dark:bg-violet-950/30' : 'text-slate-500 hover:text-slate-700'
            }`}>{t.label}</button>
        ))}
      </div>

      {tab === 'generate' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-1">
            <CardHeader><CardTitle className="text-base">Source Content</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <textarea className="w-full h-40 px-3 py-2 text-sm rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 resize-none"
                placeholder="Paste your blog post, article, or content here..." value={sourceContent} onChange={e => setSourceContent(e.target.value)} />
              <Input placeholder="URL to include (optional)" value={postUrl} onChange={e => setPostUrl(e.target.value)} />
              <select value={tone} onChange={e => setTone(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm">
                {['professional', 'casual', 'friendly', 'witty', 'inspirational'].map(t => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
              <div>
                <p className="text-xs text-slate-500 mb-2 font-medium">Platform</p>
                <div className="flex gap-2">
                  {['linkedin', 'facebook', 'twitter'].map(p => (
                    <button key={p} type="button"
                      onClick={() => setPlatform(p)}
                      className={`flex-1 py-1.5 text-xs font-semibold rounded-md border transition-colors ${
                        platform === p ? PLATFORM_ACTIVE[p] : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-400'
                      }`}>
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white" onClick={handleGenerate} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wand2 className="h-4 w-4 mr-2" />}
                Generate Posts
              </Button>
            </CardContent>
          </Card>

          <div className="lg:col-span-2 space-y-4">
            {posts.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-slate-400">Generated posts will appear here</CardContent></Card>
            ) : posts.map((post, i) => {
              const key = `gen-${post.post_id || i}`;
              const full = post.post_text || '';
              const isLong = full.length > 280;
              const isOpen = expanded[key];
              return (
              <Card key={i}>
                <CardContent className="pt-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <Badge className={PLATFORM_COLORS[post.platform] || 'bg-slate-100'}>{post.platform}</Badge>
                    <span className="text-xs text-slate-400">{post.character_count} chars · {post.content_type}</span>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3 text-sm whitespace-pre-wrap break-words">
                    {isOpen || !isLong ? full : `${full.substring(0, 280)}…`}
                    {isLong && (
                      <button type="button" onClick={() => toggleExpand(key)}
                        className="ml-2 text-xs font-medium text-violet-600 hover:text-violet-700">
                        {isOpen ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {(post.hashtags || []).map((h, j) => <Badge key={j} variant="outline" className="text-xs">#{h.replace('#', '')}</Badge>)}
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>Best time: {post.best_posting_time}</span>
                    <span>{post.engagement_tip}</span>
                  </div>
                  <div className="flex gap-2 pt-1 flex-wrap items-center">
                    <Button size="sm" variant="outline" onClick={() => copyText(post.post_text, post.post_id)}>
                      {copied === post.post_id ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                      {copied === post.post_id ? 'Copied' : 'Copy'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => updateStatus(post.post_id, 'scheduled')}>Schedule</Button>
                    {publishedInfo[post.post_id] ? (
                      <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                        <Check className="h-3 w-3" /> Published
                      </span>
                    ) : (
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => publishToApi(post.post_id)} disabled={loading}>API Publish</Button>
                    )}
                    {publishedInfo[post.post_id]?.url && (
                      <a href={publishedInfo[post.post_id].url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-md border border-blue-600 text-blue-600 hover:bg-blue-50 transition-colors">
                        View post →
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'calendar' && (
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Generate Content Calendar</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input placeholder="Business type" value={calBusiness} onChange={e => setCalBusiness(e.target.value)} />
                <Input placeholder="Topics (comma-separated)" value={calTopics} onChange={e => setCalTopics(e.target.value)} />
                <div className="flex gap-2">
                  <Input type="number" min={7} max={30} value={calDays} onChange={e => setCalDays(Number(e.target.value))} className="w-20" />
                  <Button className="flex-1 bg-violet-600 hover:bg-violet-700 text-white" onClick={handleCalendar} disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Calendar className="h-4 w-4 mr-2" />}
                    Generate
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {calendar && (
            <Card>
              <CardHeader><CardTitle className="text-base">Content Calendar</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-slate-500">{calendar.calendar_summary}</p>
                <div className="space-y-3">
                  {(calendar.days || []).map((day, i) => (
                    <div key={i} className="border rounded-lg p-3 border-slate-200 dark:border-slate-700">
                      <h4 className="font-semibold text-sm mb-2">{day.date_label}</h4>
                      <div className="space-y-2">
                        {(day.posts || []).map((p, j) => {
                          const key = `cal-${i}-${j}`;
                          const full = p.post_text || '';
                          const isLong = full.length > 150;
                          const isOpen = expanded[key];
                          return (
                            <div key={j} className="flex items-start gap-2 text-sm bg-slate-50 dark:bg-slate-900 rounded p-2">
                              <Badge className={`${PLATFORM_COLORS[p.platform] || 'bg-slate-100'} shrink-0`}>{p.platform}</Badge>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs whitespace-pre-wrap break-words">
                                  {isOpen || !isLong ? full : `${full.substring(0, 150)}…`}
                                </p>
                                {isLong && (
                                  <button type="button" onClick={() => toggleExpand(key)}
                                    className="mt-1 text-xs font-medium text-violet-600 hover:text-violet-700">
                                    {isOpen ? 'Show less' : 'Show more'}
                                  </button>
                                )}
                              </div>
                              <span className="text-xs text-slate-400 shrink-0">{p.posting_time}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {tab === 'library' && (
        <div className="space-y-4">
          <div className="flex gap-2">
            <select value={filterPlatform} onChange={e => setFilterPlatform(e.target.value)}
              className="px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm">
              <option value="">All Platforms</option>
              {['linkedin', 'facebook', 'twitter'].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm">
              <option value="">All Statuses</option>
              {['draft', 'scheduled', 'published', 'archived'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {filtered.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-slate-400">No posts yet. Generate some first!</CardContent></Card>
          ) : filtered.map((post, i) => {
            const key = `lib-${post.post_id || i}`;
            const full = post.post_text || '';
            const isLong = full.length > 280;
            const isOpen = expanded[key];
            return (
            <Card key={i}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={PLATFORM_COLORS[post.platform] || 'bg-slate-100'}>{post.platform}</Badge>
                  <Badge variant={post.status === 'published' ? 'default' : 'outline'}>{post.status}</Badge>
                  <span className="text-xs text-slate-400 ml-auto">{new Date(post.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-sm whitespace-pre-wrap break-words">
                  {isOpen || !isLong ? full : `${full.substring(0, 280)}…`}
                  {isLong && (
                    <button type="button" onClick={() => toggleExpand(key)}
                      className="ml-2 text-xs font-medium text-violet-600 hover:text-violet-700">
                      {isOpen ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </p>
                {post.hashtags?.length > 0 && (
                  <div className="flex gap-1 flex-wrap mt-2">
                    {post.hashtags.map((h, j) => <Badge key={j} variant="outline" className="text-xs">#{h.replace('#', '')}</Badge>)}
                  </div>
                )}
                <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex-wrap items-center">
                  <Button size="sm" variant="outline" onClick={() => copyText(post.post_text, post.post_id)}>
                    {copied === post.post_id ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                    {copied === post.post_id ? 'Copied' : 'Copy'}
                  </Button>
                  {post.status !== 'scheduled' && post.status !== 'published' && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(post.post_id, 'scheduled')}>Schedule</Button>
                  )}
                  {post.status !== 'published' && (
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => updateStatus(post.post_id, 'published')}>Mark Published</Button>
                  )}
                  {post.status !== 'archived' && (
                    <Button size="sm" variant="outline" className="text-red-500 hover:text-red-700" onClick={() => updateStatus(post.post_id, 'archived')}>Archive</Button>
                  )}
                  {post.published_url && (
                    <a href={post.published_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-md border border-blue-600 text-blue-600 hover:bg-blue-50 transition-colors ml-auto">
                      View post →
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
